import { useState, useEffect, useMemo, useRef } from "react";

import { UpdateMiner, DeleteMiner } from "api/farm";
import Spinner from "components/svg/Spinner";

export default function MinerProfile({ miner, isSelected, onClick, farmUrl, farmKey, selectedDecision, showPresets, onUpdate }: any) {
  const switchType = useMemo(() => miner.switchType.split("/"), [miner]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState("");
  const timeoutRef: any = useRef();

  useEffect(() => {
    setSelectedPreset(miner.decisions[selectedDecision]);
  }, [selectedDecision, miner]);

  const isCustomOff = useMemo(() =>
    selectedDecision &&
    selectedPreset &&
    !["ignore", ...switchType].includes(selectedPreset) &&
    miner.presets &&
    miner.presets.every(({ value }: any) => value !== selectedPreset), [selectedDecision, selectedPreset, switchType, miner]);

  const [offFreq, setOffFreq] = useState("0");
  const [offVolt, setOffVolt] = useState("0");

  useEffect(() => {
    if (isCustomOff && selectedPreset !== "Custom") {
      setOffFreq(selectedPreset.split("x")[0]);
      setOffVolt(selectedPreset.split("x")[1]);
    }
  }, [isCustomOff, selectedPreset]);

  const handleUpdate = (value: string) => {
    if(value === "Custom"){
      return;
    }
    setIsLoading(true);
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      UpdateMiner(farmUrl, farmKey, miner.ip, selectedDecision, value).then(() => {
        setIsLoading(false);
        onUpdate();
      });
    }, 400);
  };

  const handleCustomUpdate = (freq: string, volt: string) => {
    if ((freq !== "") && (volt !== "")) {
      handleUpdate(`${freq}x${volt}`);
    }
  };

  const handleRemove = () => {
    DeleteMiner(farmUrl, farmKey, miner.ip).then(onUpdate);
  };

  return (
    <tr>
      <td className={`border-t border-slate-100 py-3 px-2 font-light text-sm`}>
        <input type="checkbox" className="ml-4 hidden" title={`Select ${miner.ip}`} checked={isSelected} onChange={onClick} />
      </td>
      <td className={`border-t border-slate-100 py-3 px-2 font-light text-sm`}>{miner.ip}</td>
      <td className={`border-t border-slate-100 py-3 px-2 font-light text-sm`}>{miner.macAddress}</td>
      <td className={`border-t border-slate-100 py-3 px-2 font-light text-sm`}>{miner.minertype}</td>
      <td className={`border-t border-slate-100 py-3 px-2 font-light text-sm`}>
        {showPresets ? (
          <div className="show-spinner">
            <select
              className={`bg-white border ${isLoading ? "border-red-500 text-red-500" : "border-slate-200"} w-full font-normal rounded-sm px-2 py-2 text-xs`}
              title={`Select profile off for ${miner.ip}`}
              onChange={({ target: { value } }) => {
                setSelectedPreset(value);
                handleUpdate(value);
              }}
              value={isCustomOff ? "Custom" : selectedPreset}
            >
              <option>ignore</option>
              <option>{switchType[0]}</option>
              <option>{switchType[1]}</option>
              {miner.presets
                ? miner.presets.map((preset: any, index: number) => {
                    if (["profile_user", "disabled", "custom"].includes(preset.value)) {
                      return null;
                    }

                    return (
                      <option key={index} value={preset.value}>
                        {preset.name}
                      </option>
                    );
                  })
                : null}
              {miner.hasCustomPresets ? <option value="Custom">Custom</option> : null}
            </select>
            <Spinner className="block mx-auto h-4 w-4 text-gray-200" />
          </div>
        ) : null}
        {isCustomOff && showPresets ? (
          <>
            <div className="mt-3 grid grid-cols-6 gap-4 items-center">
              <label className="col-span-2 text-xs">Frequency</label>
              <input
                type="number"
                placeholder="Profile frequency"
                className={`form-input px-3 py-2 border ${isLoading ? "border-red-500 outline-none" : "border-slate-200"} rounded-sm text-xs col-span-4`}
                value={offFreq}
                min="0"
                onChange={(e) => {
                  setOffFreq(e.target.value);
                  handleCustomUpdate(e.target.value, offVolt);
                }}
              />
            </div>
            <div className="mt-3 grid grid-cols-6 gap-4 items-center">
              <label className="col-span-2 text-xs">Voltage</label>
              <input
                type="number"
                placeholder="Profile voltage"
                className={`form-input px-3 py-2 border ${isLoading ? "border-red-500 outline-none" : "border-slate-200"} text-xs rounded-sm col-span-4`}
                value={offVolt}
                min="0"
                onChange={(e) => {
                  setOffVolt(e.target.value);
                  handleCustomUpdate(offFreq, e.target.value);
                }}
              />
            </div>
          </>
        ) : null}
      </td>
      <td className={`border-t border-slate-100 py-3 px-2 font-light text-sm`}>
        <div className="show-spinner">
          <div
            className="border-slate-200 text-center text-xs font-medium block text-pink-600 py-1 cursor-pointer"
            role="menuitem"
            title={`Remove ${miner.ip}`}
            onClick={handleRemove}
          >
            Remove
          </div>
          <Spinner className="block mx-auto h-4 w-4 text-gray-200" />
        </div>
      </td>
    </tr>
  );
}
